'use client'

// Full-catalogue tag picker for the post composer — the same keyword set the
// search filters offer (keywords_cz via useKeywordsStore), grouped by
// category. Falls back to the mock pools when the catalogue isn't loaded
// (offline demo). User decision 2026-07-15: sellers may tag ANYTHING seekers
// can filter by.
import * as React from 'react'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Chip, IconBtn, RModal, pillBtnBase, pretty } from '@/components/roostr'
import { useKeywordsStore } from '@/lib/store'
import { useI18n } from '@/lib/i18n'
import { FEATURE_EXTRA, FEATURE_POOL } from './post-data'

export interface TagPickerModalProps {
  open: boolean
  onClose: () => void
  selected: string[]
  onToggle: (key: string) => void
}

/** feature_key → localized label map from the loaded catalogue (pretty() fallback). */
export function useTagLabels(): (key: string) => string {
  const { keywords } = useKeywordsStore()
  const map = React.useMemo(() => {
    const m = new Map<string, string>()
    for (const cat of Object.values(keywords?.categories ?? {})) {
      for (const kw of cat.keywords) m.set(kw.feature_key, kw.label)
    }
    return m
  }, [keywords])
  return React.useCallback((key: string) => map.get(key) ?? pretty(key), [map])
}

export function TagPickerModal({ open, onClose, selected, onToggle }: TagPickerModalProps) {
  const { t } = useI18n()
  const { keywords } = useKeywordsStore()
  const [query, setQuery] = React.useState('')

  const groups = React.useMemo(() => {
    const cats = Object.values(keywords?.categories ?? {})
    if (cats.length) {
      return cats
        .map((c) => ({
          name: c.display_name || pretty(c.name),
          tags: c.keywords.map((k) => ({ key: k.feature_key, label: k.label })),
        }))
        .filter((g) => g.tags.length)
    }
    // Demo fallback — catalogue unavailable.
    return [
      {
        name: t('post.tagFallbackGroup', 'Common tags'),
        tags: FEATURE_POOL.concat(FEATURE_EXTRA).map((k) => ({ key: k, label: pretty(k) })),
      },
    ]
  }, [keywords, t])

  const q = query.trim().toLocaleLowerCase()
  const shown = q
    ? groups
        .map((g) => ({ ...g, tags: g.tags.filter((tag) => tag.label.toLocaleLowerCase().includes(q)) }))
        .filter((g) => g.tags.length)
    : groups

  return (
    <RModal open={open} onClose={onClose} width={560} mobileSheet ariaLabel={t('post.tagPickerTitle', 'All tags')}>
      <div className="flex items-center gap-3">
        <div>
          <div className="text-[16.5px] font-bold tracking-[-0.2px]">{t('post.tagPickerTitle', 'All tags')}</div>
          <div className="mt-px text-[12px] text-ink-muted">
            {t('post.tagPickerHint', 'Everything seekers can filter by — tap to add to your listing.')}
          </div>
        </div>
        <span className="flex-1" />
        <IconBtn size={32} title={t('search.close', 'Close')} onClick={onClose} className="bg-surface-high">
          <X size={16} className="text-ink-secondary" />
        </IconBtn>
      </div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('post.tagPickerSearch', 'Search tags…')}
        className="mt-3.5 w-full rounded-full border border-outline bg-surface-low px-[13px] py-[8px] text-[13px] text-ink outline-none placeholder:text-ink-muted"
      />
      <div className="mt-3 flex max-h-[52vh] flex-col gap-3.5 overflow-y-auto pb-1 pr-1">
        {shown.map((g) => (
          <div key={g.name}>
            <div className="mb-[6px] font-mono text-[10px] font-bold uppercase tracking-[0.7px] text-ink-muted">
              {g.name}
            </div>
            <div className="flex flex-wrap gap-[6px]">
              {g.tags.map(({ key, label }) => (
                <Chip key={key} small label={label} active={selected.includes(key)} onClick={() => onToggle(key)} />
              ))}
            </div>
          </div>
        ))}
        {!shown.length ? (
          <div className="py-6 text-center text-[12.5px] text-ink-muted">
            {t('post.tagPickerEmpty', 'No tags match that search.')}
          </div>
        ) : null}
      </div>
      <div className="mt-3.5 flex items-center gap-3 border-t border-outline-variant pt-3">
        <span className="text-[12px] text-ink-muted">
          {selected.length} {t('post.tagPickerSelected', 'selected')}
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className={cn(pillBtnBase, 'bg-primary-container px-[18px] py-[8px] text-[13px] text-on-primary-container')}
        >
          <Check size={15} /> {t('post.tagPickerDone', 'Done')}
        </button>
      </div>
    </RModal>
  )
}
